import { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MeContext } from '../../../context/MeContext';
import { useAppDispatch } from '../../../store/redux/hooks';
import { useGetMeQuery } from '../../../store/redux/api/accountApi';
import { useGetOnboardingStateQuery } from '../../../store/redux/api/onboardingApi';
import { templateApi } from '../../../store/redux/api/templateApi';
import { useListPassesQuery } from '../../../store/redux/api/passApi';
import type {
  OnboardingNextStep,
  OnboardingStepKey,
} from '../../../types/onboarding';
import OnboardingStepCompletionModal, {
  type StepModalConfig,
} from './OnboardingStepCompletionModal';
import OnboardingFinalModal from './OnboardingFinalModal';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';

const PREV_STEP_BY_NEXT: Record<OnboardingNextStep, OnboardingStepKey | null> =
  {
    create_template: null,
    create_pass: 'template_created',
    add_to_wallet: 'pass_created',
    send_notification: 'pass_added_to_wallet',
    complete: 'notification_sent',
  };

const STEP_MODAL_CONFIGS: Record<
  Exclude<OnboardingStepKey, 'notification_sent'>,
  StepModalConfig
> = {
  template_created: {
    stepNumber: 1,
    completedLabel: 'Template created',
    tagline: 'Your pass design looks great!',
    encouragement: 'Time to bring it to life',
    upNextDescription:
      "Now your template is ready – let's create a pass from it. Personalise it with any details you want and hit save.",
    eta: 'Takes about 2 minutes',
    ctaLabel: 'Create a pass',
  },
  pass_created: {
    stepNumber: 2,
    completedLabel: 'First pass issued',
    tagline: 'Your first pass is ready!',
    encouragement: 'Time to get it into a wallet',
    upNextDescription:
      'Open the install link on your phone or scan the QR code with your camera – see it exactly the way your customers will.',
    ctaLabel: 'Add to wallet',
    eta: 'Quick one – under a minute',
  },
  pass_added_to_wallet: {
    stepNumber: 3,
    completedLabel: 'Pass added',
    tagline: 'It looks great in your wallet!',
    encouragement: "Here's the best part.",
    upNextDescription:
      "Send a push notification and watch it arrive on your phone's lock screen in real time.",
    ctaLabel: 'Send now',
    eta: 'Final step – only takes a minute',
  },
};

const FIRST_PASS_IMPROVEMENTS_CONFIG: StepModalConfig = {
  stepNumber: 1,
  completedLabel: 'Template created',
  tagline: 'Your template looks great!',
  encouragement: "The hard part's done",
  upNextDescription:
    'One design, a pass for every customer – in bulk or via API. Make the first one yourself: fill in the fields and watch it become a real pass.',
  eta: 'Takes about 2 minutes',
  ctaLabel: 'Create my first pass',
};

const SEEN_KEY = (accountId: string, step: OnboardingStepKey) =>
  `onb_modal_seen_${accountId}_${step}`;
const BASELINE_KEY = (accountId: string) =>
  `onb_baseline_next_step_${accountId}`;

const markSeen = (accountId: string, step: OnboardingStepKey) => {
  try {
    localStorage.setItem(SEEN_KEY(accountId, step), '1');
  } catch {
    // localStorage can throw in private modes / quota errors; soft-fail.
  }
};

const wasSeen = (accountId: string, step: OnboardingStepKey) => {
  try {
    return localStorage.getItem(SEEN_KEY(accountId, step)) === '1';
  } catch {
    return false;
  }
};

const OnboardingModal = () => {
  const isReadOnly = useIsReadOnly();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const meCtx = useContext(MeContext) as {
    state: {
      account: { id?: string | number } | null;
      onboardingChecklistEnabled: boolean | null;
      onboardingCompleted: boolean | null;
      firstPassImprovementsEnabled: boolean | null;
      passTemplates: { uuid?: string }[] | null;
    };
  };

  const flagOn = meCtx.state.onboardingChecklistEnabled;
  const alreadyComplete = meCtx.state.onboardingCompleted;
  const firstPassImprovements = meCtx.state.firstPassImprovementsEnabled;
  const { isFetching: isMeFetching } = useGetMeQuery(undefined);
  const accountId =
    meCtx.state.account?.id != null
      ? String(meCtx.state.account.id)
      : undefined;

  const { data } = useGetOnboardingStateQuery(undefined, {
    skip: !flagOn || alreadyComplete,
    refetchOnFocus: true,
  });

  const activeStep = data?.next_step;
  const { data: recentCreatedPasses } = useListPassesQuery(
    { perPage: 1, sortBy: 'createdAt', sortOrder: 'desc' },
    { skip: activeStep !== 'add_to_wallet' }
  );
  const { data: recentActivePasses } = useListPassesQuery(
    {
      perPage: 1,
      filters: { status: ['active'] },
      sortBy: 'addedAt',
      sortOrder: 'desc',
    },
    { skip: activeStep !== 'send_notification' }
  );

  const walletPassId = recentCreatedPasses?.data?.[0]?.id;
  const notificationPassId = recentActivePasses?.data?.[0]?.id;

  const [openStep, setOpenStep] = useState<OnboardingStepKey | null>(null);

  useEffect(() => {
    if (!accountId) return;

    const baselineKey = BASELINE_KEY(accountId);
    const baseline = localStorage.getItem(
      baselineKey
    ) as OnboardingNextStep | null;

    if (
      alreadyComplete &&
      baseline &&
      baseline !== 'complete' &&
      !wasSeen(accountId, 'notification_sent')
    ) {
      setOpenStep('notification_sent');
      return;
    }

    if (!data) return;

    if (!baseline) {
      localStorage.setItem(baselineKey, data.next_step);
      return;
    }

    if (baseline === data.next_step) return;

    const completedStep = PREV_STEP_BY_NEXT[data.next_step];
    if (!completedStep) {
      localStorage.setItem(baselineKey, data.next_step);
      return;
    }
    if (wasSeen(accountId, completedStep)) {
      localStorage.setItem(baselineKey, data.next_step);
      return;
    }

    setOpenStep(completedStep);
  }, [data, alreadyComplete, accountId]);

  const onlyTemplateUuid =
    meCtx.state.passTemplates?.length === 1
      ? meCtx.state.passTemplates[0]?.uuid
      : undefined;
  useEffect(() => {
    if (openStep !== 'template_created' || !firstPassImprovements) return;
    if (!onlyTemplateUuid) return;
    dispatch(
      templateApi.util.prefetch('getPassTemplate', { id: onlyTemplateUuid }, {})
    );
  }, [openStep, firstPassImprovements, onlyTemplateUuid, dispatch]);

  if (!flagOn) return null;
  if (isReadOnly) return null;
  if (alreadyComplete && openStep !== 'notification_sent') return null;
  if (!openStep) return null;
  if (openStep === 'template_created' && isMeFetching) return null;

  const close = () => {
    if (openStep && accountId) markSeen(accountId, openStep);
    setOpenStep(null);
  };

  const ctaTargetFor = (step: OnboardingStepKey): string => {
    if (step === 'template_created') return '/passes/create';
    if (step === 'pass_created' && walletPassId) {
      return `/passes/${walletPassId}?highlight=wallet`;
    }
    if (step === 'pass_added_to_wallet' && notificationPassId) {
      return `/passes/${notificationPassId}?highlight=notification`;
    }
    return '/passes';
  };

  if (openStep === 'notification_sent') {
    return (
      <OnboardingFinalModal
        isOpen
        onClose={close}
        onGoLive={() => {
          close();
          navigate('/my-account?openGoLive=1');
        }}
      />
    );
  }

  const stepConfig =
    openStep === 'template_created' && firstPassImprovements
      ? FIRST_PASS_IMPROVEMENTS_CONFIG
      : STEP_MODAL_CONFIGS[openStep];
  return (
    <OnboardingStepCompletionModal
      isOpen
      onClose={close}
      onCta={() => {
        const target = ctaTargetFor(openStep);
        close();
        navigate(target);
      }}
      config={stepConfig}
    />
  );
};

export default OnboardingModal;
